import React, { useState, forwardRef, KeyboardEvent, useEffect, useRef, useImperativeHandle } from 'react';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

// Define the type for a validator function
export type TagValidator = (tag: string) => boolean;

// Define the props for the TagInput component
export interface TagInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  placeholder?: string;
  tags: string[];
  setTags: (tags: string[]) => void;
  validators?: { validatorFn: TagValidator, errorMessage: string }[];
  disabled?: boolean;
  error?: {message?: string, code?: string};
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const [background, text, shadow] = ["bg-grey-200 dark:bg-grey-500", "text-text-high-emphasis", ""]
export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(
  ({ tags, setTags, validators = [], error, className, placeholder, disabled, ...props }, ref) => {
    const [inputValue, setInputValue] = useState('');
    const [inHouseTags, setInHouseTags] = useState<string[]>([]);
    const [showError, setShowError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(()=>{
      if(!!tags.length && !inHouseTags.length){
        setInHouseTags(tags);
      }
      setTags(inHouseTags);
      if(!inHouseTags.length){
        setShowError(false);
        setErrorMsg('');
      }
    }, [inHouseTags, tags])

    useEffect(()=>{
      const vlaue = inputValue.trim();
      if(!vlaue && !!(showError || error)){
        setShowError(false);
        setErrorMsg('');
      }else if(vlaue && !!(showError || error)){
        validateInput(vlaue);
      }
    }, [inputValue])

    useEffect(() => {
      if(isFocused && inputRef.current){
        inputRef.current?.focus();
      }
    }, [isFocused])

    useEffect(()=>{
      if(error?.message && !!inHouseTags.length){
        setShowError(true);
        setErrorMsg(error.message);
      }
    }, [error]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e?.preventDefault?.();
        const newTag = inputValue.trim();

        // Check for duplicates and run validators
        const isDuplicate = inHouseTags.includes(newTag);
        const isValid = validateInput(newTag);

        if (!isDuplicate && isValid) {
          setInHouseTags([...inHouseTags, newTag]);
          setInputValue('');
        }else if(!isValid){
          setShowError(true);
        }
        if (isDuplicate) {
          setShowError(true);
          setErrorMsg('Email already added');
        }
      }
      // Remove the last tag on backspace if the input is empty
      else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
        setInHouseTags(tags.slice(0, -1));
      }
    };

    const removeTag = (tagToRemove: string) => {
        const t = inHouseTags.filter(tag => tag !== tagToRemove)
        console.log('t tags', t)
        setInHouseTags(t);
    };

    const lastTag = inHouseTags.length > 0 ? inHouseTags[inHouseTags.length - 1] : null;

    const validateInput = (value: string) => {
      let isValid = false;
      setShowError(!validators.every((validator) => {
        try{
          isValid = validator.validatorFn(value);
          if(!isValid){
            setErrorMsg(validator.errorMessage);
          }
          return isValid;
        }catch(e){
          console.log(e);
          return isValid;
        }
      }));
      return isValid;
    }

    return (
      <div className={`${className}`}>
        <div
          className={cn(
            'flex items-center justify-between w-full h-10 py-1 text-sm border rounded-md gap-1',
            `${!!inHouseTags.length ? 'px-1' : 'px-2'}`,
            'bg-background ring-offset-background ',
            (showError) ? 'border-text-error' : 'border-input'
            
          )}
        >
          <div className={`sa-taginput-container h-full flex items-center gap-1 flex-grow ${!!inHouseTags.length ? 'w-[calc(100%-28px)]' : 'w-full'} `} onClick={()=>!disabled && setIsFocused(true)}>
            
            {!isFocused && !!inHouseTags.length && inHouseTags.slice(-2).map((tag, index) => (
              <Badge key={index} variant="outline" className={`sa-taginput-tag h-full flex-shrink-0 rounded-[6px] ${inHouseTags.slice(-2).length > 1 ? 'max-w-[calc(50%-1px)]' : 'max-w-full'} ${background} ${text} ${shadow}`}>
                <span className="truncate" title={tag}>{tag}</span>
                {
                  disabled
                  ? <></>
                  : <Button variant="ghost" className="hover:bg-transparent cursor-pointer p-0 h-4 w-1" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeTag(tag);
                  }}>
                    <X
                      className=""
                      size={6}
                    />
                    </Button>
                }
              </Badge>
            ))}

            {
              (disabled)
              ? <></>
              : <input
                  {...props}
                  ref={inputRef}
                  type="text"
                  disabled={disabled}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsFocused(true)}
                  style={{
                    display: isFocused || !inHouseTags.length ? 'block' : 'none'
                  }}
                  onBlur={() => {
                    if(!showError){
                      setIsFocused(false);
                    }
                    handleKeyDown({ key: 'Enter' } as KeyboardEvent<HTMLInputElement>);
                  }}
                  placeholder={placeholder}
                  className={`flex-grow bg-transparent outline-none placeholder:text-muted-foreground ${lastTag && !isFocused ? 'max-w-[0] sm:max-w-[60%]' : 'md:max-w-[100%]'}`}
                />
            }
            
          </div>

          {/* Tag count badge */}
          {((isFocused && !!inHouseTags.length) || inHouseTags.length > 2) && (
            <div className="relative hidden sm:flex">
                <Badge variant="outline" onClick={()=>setShowTooltip(true)} className={`flex-shrink-0 rounded-[6px] cursor-pointer ${background} ${text} ${shadow}`}>
                {isFocused ? inHouseTags.length : inHouseTags.length-2}
                </Badge>
                
                {showTooltip && <div onMouseEnter={()=>setShowTooltip(true)} onMouseLeave={()=>setShowTooltip(false)} className="absolute right-[25%] top-[100%] bg-grey-200 dark:bg-grey-500 rounded-[6px] border flex items-center justify-center p-1">
                  <div className="">
                    {((!isFocused && inHouseTags.length > 2) ? inHouseTags.slice(0, inHouseTags.length-2) : inHouseTags).map((tag, index)=>{
                      return <div className="p-1 hover:bg-tint-secondary rounded-[6px] cursor-pointer flex items-center justify-between " key={index}>
                        <span>{tag}</span>
                        <X size={12} className="cursor-pointer rounded-full ml-2 p-1 hover:bg-tint-primary h-5 w-5" onClick={()=>removeTag(tag)}/>
                      </div>
                    })}
                  </div>
                </div>}
            </div>
          )}
        </div>
        {showError && errorMsg && <p className="ml-2 text-sm text-red-500">{errorMsg}</p>}
      </div>
    );
  }
);

TagInput.displayName = 'TagInput';
